import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Search, ChevronLeft, ChevronRight, Archive, FolderPlus, Pencil, X } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api, apiError } from "@/lib/api";
import { Loading, EmptyState, Badge, Btn, Input, Select, Field, Textarea, PageHeader, ContactMethodsEditor, Collapsible } from "@/components/common";
import Modal from "@/components/Modal";

const ETYPES = ["person", "business", "organization", "place", "group", "event"];
const VERIFS = ["unverified", "uncertain", "confirmed", "disputed", "outdated"];
const STAGES = ["candidate", "verified_identity", "documented_connection", "established_relationship", "communication_permitted"];

export default function Entities() {
  const { workspaceId, canEdit } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [groups, setGroups] = useState([]);
  const [f, setF] = useState({ search: "", type: "", verification_status: "", relationship_stage: "", importance: "", group_id: "", archived: false, page: 1 });
  const [createOpen, setCreateOpen] = useState(false);
  const [sel, setSel] = useState({});
  const [bulkModal, setBulkModal] = useState(null); // "group" | "edit"

  const load = useCallback(async () => {
    if (!workspaceId) return;
    const p = new URLSearchParams({ workspace_id: workspaceId, search: f.search, type: f.type, verification_status: f.verification_status, relationship_stage: f.relationship_stage, importance: f.importance, group_id: f.group_id, archived: f.archived, page: f.page, limit: 25 });
    const { data } = await api.get(`/entities?${p}`);
    setData(data);
  }, [workspaceId, f]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (workspaceId) api.get(`/groups?workspace_id=${workspaceId}`).then((r) => setGroups(r.data.items || [])).catch(() => {}); }, [workspaceId]);

  const pages = data ? Math.max(1, Math.ceil(data.total / data.limit)) : 1;
  const selectedIds = Object.keys(sel).filter((k) => sel[k]);
  const toggleAll = () => {
    if (!data) return;
    if (data.items.every((e) => sel[e.id])) { const n = { ...sel }; data.items.forEach((e) => delete n[e.id]); setSel(n); }
    else { const n = { ...sel }; data.items.forEach((e) => (n[e.id] = true)); setSel(n); }
  };
  const selectedEntities = (data?.items || []).filter((e) => sel[e.id]);

  return (
    <div className="p-6 md:p-8 max-w-7xl mx-auto">
      <PageHeader title="Entity Files" sub="Unified records for people, businesses, organizations, places, groups, and events."
        actions={canEdit && <Btn variant="primary" data-testid="new-entity-btn" onClick={() => setCreateOpen(true)}><Plus size={15} /> New entity</Btn>} />

      <div className="flex flex-wrap items-center gap-2 mb-4">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2" style={{ color: "var(--si-text-2)" }} />
          <Input data-testid="entity-search" value={f.search} onChange={(e) => setF((p) => ({ ...p, search: e.target.value, page: 1 }))} placeholder="Search name, alias, tag…" style={{ paddingLeft: 30 }} />
        </div>
        <Select data-testid="entity-filter-type" value={f.type} onChange={(e) => setF((p) => ({ ...p, type: e.target.value, page: 1 }))} style={{ width: "auto" }}><option value="">All types</option>{ETYPES.map((t) => <option key={t} value={t}>{t}</option>)}</Select>
        <Select value={f.verification_status} onChange={(e) => setF((p) => ({ ...p, verification_status: e.target.value, page: 1 }))} style={{ width: "auto" }}><option value="">All verification</option>{VERIFS.map((t) => <option key={t} value={t}>{t}</option>)}</Select>
        <Select value={f.relationship_stage} onChange={(e) => setF((p) => ({ ...p, relationship_stage: e.target.value, page: 1 }))} style={{ width: "auto" }}><option value="">All stages</option>{STAGES.map((t) => <option key={t} value={t}>{t.replace(/_/g, " ")}</option>)}</Select>
        <Select data-testid="entity-filter-importance" value={f.importance} onChange={(e) => setF((p) => ({ ...p, importance: e.target.value, page: 1 }))} style={{ width: "auto" }}><option value="">All importance</option>{["critical", "high", "medium", "low", "unassessed"].map((t) => <option key={t} value={t}>{t}</option>)}</Select>
        <Select data-testid="entity-filter-group" value={f.group_id} onChange={(e) => setF((p) => ({ ...p, group_id: e.target.value, page: 1 }))} style={{ width: "auto" }}><option value="">All groups</option>{groups.map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}</Select>
        <Btn variant={f.archived ? "secondary" : "ghost"} data-testid="toggle-archived" onClick={() => setF((p) => ({ ...p, archived: !p.archived, page: 1 }))}><Archive size={14} /> {f.archived ? "Archived" : "Active"}</Btn>
      </div>

      {canEdit && selectedIds.length > 0 && (
        <div className="flex items-center gap-2 mb-3 px-3 py-2" style={{ background: "var(--si-surface)", border: "1px solid var(--si-border)", borderRadius: 2 }} data-testid="entity-bulk-bar">
          <span className="si-label">{selectedIds.length} selected</span>
          <Btn variant="secondary" className="!py-1 !px-3 text-xs" onClick={() => setBulkModal("group")} data-testid="bulk-add-group-btn"><FolderPlus size={13} /> Add to group</Btn>
          <Btn variant="secondary" className="!py-1 !px-3 text-xs" onClick={() => setBulkModal("edit")} data-testid="bulk-edit-btn"><Pencil size={13} /> Bulk edit</Btn>
          <button onClick={() => setSel({})} className="si-label ml-auto" data-testid="bulk-clear">clear</button>
        </div>
      )}

      {!data ? <Loading /> : data.items.length === 0 ? (
        <EmptyState icon={Search} title="No entities found" hint="Create a new entity or adjust your filters." />
      ) : (
        <div style={{ border: "1px solid var(--si-border)", borderRadius: 2, overflow: "hidden" }}>
          <table className="w-full text-sm">
            <thead style={{ background: "var(--si-surface)" }}>
              <tr style={{ borderBottom: "1px solid var(--si-border)" }}>
                {canEdit && <th className="px-3 py-2.5 w-8"><input type="checkbox" checked={data.items.length > 0 && data.items.every((e) => sel[e.id])} onChange={toggleAll} data-testid="entity-select-all" /></th>}
                {["Name", "Type", "Verification", "Stage", "Tags", "Updated"].map((h) => <th key={h} className="text-left px-4 py-2.5 si-label font-normal">{h}</th>)}
              </tr>
            </thead>
            <tbody>
              {data.items.map((e) => (
                <tr key={e.id} data-testid={`entity-row-${e.id}`} className="transition-colors duration-200"
                  style={{ borderBottom: "1px solid var(--si-border)" }}>
                  {canEdit && <td className="px-3 py-2.5"><input type="checkbox" checked={!!sel[e.id]} onChange={(ev) => setSel((s) => ({ ...s, [e.id]: ev.target.checked }))} data-testid={`entity-select-${e.id}`} /></td>}
                  <td className="px-4 py-2.5 cursor-pointer" onClick={() => navigate(`/app/entities/${e.id}`)} style={{ color: "var(--si-text)" }}>{e.name}{e.is_sample && <span className="si-label ml-2">sample</span>}</td>
                  <td className="px-4 py-2.5 font-mono text-xs cursor-pointer" onClick={() => navigate(`/app/entities/${e.id}`)} style={{ color: "var(--si-text-2)" }}>{e.type}</td>
                  <td className="px-4 py-2.5"><Badge value={e.verification_status} /></td>
                  <td className="px-4 py-2.5 font-mono text-xs" style={{ color: "var(--si-text-2)" }}>{(e.relationship_stage || "").replace(/_/g, " ")}</td>
                  <td className="px-4 py-2.5 text-xs" style={{ color: "var(--si-text-2)" }}>{(e.tags || []).slice(0, 3).join(", ")}</td>
                  <td className="px-4 py-2.5 font-mono text-xs" style={{ color: "var(--si-text-2)" }}>{new Date(e.updated_at).toLocaleDateString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {data && data.total > data.limit && (
        <div className="flex items-center justify-between mt-4">
          <div className="si-label">{data.total} records · page {data.page} / {pages}</div>
          <div className="flex gap-2">
            <Btn variant="ghost" disabled={f.page <= 1} onClick={() => setF((p) => ({ ...p, page: p.page - 1 }))} data-testid="page-prev"><ChevronLeft size={15} /></Btn>
            <Btn variant="ghost" disabled={f.page >= pages} onClick={() => setF((p) => ({ ...p, page: p.page + 1 }))} data-testid="page-next"><ChevronRight size={15} /></Btn>
          </div>
        </div>
      )}

      {createOpen && <CreateEntityModal workspaceId={workspaceId} onClose={() => setCreateOpen(false)} onCreated={(id) => navigate(`/app/entities/${id}`)} />}
      {bulkModal === "group" && <BulkAddGroupModal workspaceId={workspaceId} entityIds={selectedIds} groups={groups} onClose={() => setBulkModal(null)} onSaved={() => { setBulkModal(null); setSel({}); }} />}
      {bulkModal === "edit" && <BulkEditModal workspaceId={workspaceId} entities={selectedEntities} onClose={() => setBulkModal(null)} onSaved={() => { setBulkModal(null); setSel({}); load(); }} />}
    </div>
  );
}

function BulkAddGroupModal({ workspaceId, entityIds, groups, onClose, onSaved }) {
  const [groupId, setGroupId] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (!groupId) return toast.error("Pick a group");
    setBusy(true);
    try {
      const { data } = await api.post(`/groups/${groupId}/members?workspace_id=${workspaceId}`, { entity_ids: entityIds, note });
      toast.success(`${data.added} added${data.skipped_existing ? `, ${data.skipped_existing} already in group` : ""}`); onSaved();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); setBusy(false); }
  };
  return (
    <Modal open onClose={onClose} title={`Add ${entityIds.length} contact(s) to a group`} testid="bulk-add-group-modal">
      <Field label="Group"><Select value={groupId} onChange={(e) => setGroupId(e.target.value)} data-testid="bulk-group-select"><option value="">— select —</option>{groups.map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}</Select></Field>
      <Field label="Membership note (optional)"><Input value={note} onChange={(e) => setNote(e.target.value)} /></Field>
      <Btn variant="primary" className="w-full" onClick={save} disabled={busy} data-testid="bulk-group-save">{busy ? "Adding…" : "Add to group"}</Btn>
    </Modal>
  );
}

function BulkEditModal({ workspaceId, entities, onClose, onSaved }) {
  const [tagMode, setTagMode] = useState("add"); // add | remove | replace
  const [tagsStr, setTagsStr] = useState("");
  const [names, setNames] = useState(() => Object.fromEntries(entities.map((e) => [e.id, e.name])));
  const [busy, setBusy] = useState(false);
  const tags = tagsStr.split(",").map((t) => t.trim()).filter(Boolean);
  const previewTags = (e) => {
    const cur = e.tags || [];
    if (tags.length === 0) return cur;
    if (tagMode === "add") return Array.from(new Set([...cur, ...tags]));
    if (tagMode === "remove") return cur.filter((t) => !tags.includes(t));
    return tags;
  };
  const save = async () => {
    setBusy(true);
    let ok = 0;
    try {
      for (const e of entities) {
        const { data: fresh } = await api.get(`/entities/${e.id}?workspace_id=${workspaceId}`);
        const body = {
          name: (names[e.id] ?? fresh.name).trim() || fresh.name, type: fresh.type, description: fresh.description || "",
          aliases: fresh.aliases || [], tags: previewTags(fresh), websites: fresh.websites || [],
          relevance_notes: fresh.relevance_notes || "", contact_methods: fresh.contact_methods || [],
          address: fresh.address || "", lat: fresh.lat ?? null, lng: fresh.lng ?? null,
          external_refs: fresh.external_refs || [], verification_status: fresh.verification_status,
          relationship_stage: fresh.relationship_stage, communication_preference: fresh.communication_preference || "",
          do_not_contact: !!fresh.do_not_contact, notes: fresh.notes || "", meaningful_dates: fresh.meaningful_dates || [],
          strategic_interest_ids: fresh.strategic_interest_ids || [],
        };
        await api.put(`/entities/${e.id}?workspace_id=${workspaceId}&version=${fresh.version}`, body);
        ok += 1;
      }
      toast.success(`Updated ${ok} contact(s)`); onSaved();
    } catch (err) { toast.error(apiError(err.response?.data?.detail) || "Some updates failed"); setBusy(false); }
  };
  return (
    <Modal open onClose={onClose} title={`Bulk edit ${entities.length} contact(s)`} width={620} testid="bulk-edit-modal">
      <div className="mb-3 p-3 text-xs" style={{ background: "var(--si-bg)", border: "1px solid var(--si-border)", borderRadius: 2, color: "var(--si-text-2)" }}>
        Edit each name individually below, and apply one tag operation to all. Changes save per-contact with version checks (no duplicate writes on retry). Each save creates a normal history entry.
      </div>
      <div className="grid grid-cols-[1fr_auto] gap-2 items-end mb-3">
        <Field label="Tag operation"><Select value={tagMode} onChange={(e) => setTagMode(e.target.value)} data-testid="bulk-tag-mode"><option value="add">Add tags</option><option value="remove">Remove tags</option><option value="replace">Replace all tags</option></Select></Field>
        <Field label="Tags (comma separated)"><Input value={tagsStr} onChange={(e) => setTagsStr(e.target.value)} placeholder="finance, prospect" data-testid="bulk-tags-input" /></Field>
      </div>
      <div className="max-h-72 overflow-y-auto" style={{ border: "1px solid var(--si-border)", borderRadius: 2 }}>
        {entities.map((e) => (
          <div key={e.id} className="px-3 py-2" style={{ borderBottom: "1px solid var(--si-border)" }} data-testid={`bulk-row-${e.id}`}>
            <Input value={names[e.id] ?? ""} onChange={(ev) => setNames((n) => ({ ...n, [e.id]: ev.target.value }))} data-testid={`bulk-name-${e.id}`} />
            <div className="si-label mt-1">tags → {previewTags(e).join(", ") || "—"}</div>
          </div>
        ))}
      </div>
      <Btn variant="primary" className="w-full mt-3" onClick={save} disabled={busy} data-testid="bulk-edit-save">{busy ? "Saving…" : "Apply changes"}</Btn>
    </Modal>
  );
}

const DRAFT_KEY = "si:entityDraft";

export function CreateEntityModal({ workspaceId, onClose, onCreated, defaultType = "business", defaultOriginId = "", defaultGroupId = "", contextLabel = "" }) {
  const blank = {
    name: "", type: defaultType, description: "", address: "", website: "",
    contact_methods: [], tags: "", lat: "", lng: "",
    verification_status: "unverified", relationship_stage: "candidate",
    originId: defaultOriginId || "", groupIds: defaultGroupId ? [defaultGroupId] : [],
  };
  const [f, setF] = useState(blank);
  const [busy, setBusy] = useState(false);
  const [origins, setOrigins] = useState([]);
  const [groups, setGroups] = useState([]);
  const [draftRestored, setDraftRestored] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  useEffect(() => {
    api.get(`/origins?workspace_id=${workspaceId}`).then((r) => setOrigins(Array.isArray(r.data) ? r.data : (r.data.items || []))).catch(() => {});
    api.get(`/groups?workspace_id=${workspaceId}`).then((r) => setGroups(Array.isArray(r.data) ? r.data : (r.data.items || []))).catch(() => {});
    try {
      const raw = localStorage.getItem(DRAFT_KEY);
      if (raw) {
        const d = JSON.parse(raw);
        if (d && d.ts && Date.now() - d.ts < 86400000 && d.workspaceId === workspaceId) {
          setF((p) => ({ ...p, ...d.form }));
          setDraftRestored(true);
          toast.message("Draft restored — finish saving your contact.");
        }
        localStorage.removeItem(DRAFT_KEY);
      }
    } catch (e) { /* ignore */ }
  }, [workspaceId]);

  const stashDraft = () => {
    try { localStorage.setItem(DRAFT_KEY, JSON.stringify({ ts: Date.now(), workspaceId, form: f })); } catch (e) { /* ignore */ }
  };

  const save = async () => {
    if (!f.name.trim()) return toast.error("Name is required");
    setBusy(true);
    try {
      const { data: ent } = await api.post(`/entities?workspace_id=${workspaceId}`, {
        name: f.name.trim(), type: f.type, description: f.description,
        contact_methods: f.contact_methods,
        websites: f.website.trim() ? [f.website.trim()] : [],
        address: f.address,
        tags: f.tags.split(",").map((t) => t.trim()).filter(Boolean),
        lat: f.lat === "" ? null : parseFloat(f.lat), lng: f.lng === "" ? null : parseFloat(f.lng),
        verification_status: f.verification_status, relationship_stage: f.relationship_stage,
      });
      // optional Spider (origin) membership
      if (f.originId) {
        try { await api.post(`/memberships?workspace_id=${workspaceId}`, { origin_id: f.originId, entity_id: ent.id }); }
        catch (e) { toast.message("Contact saved; could not add to the selected network."); }
      }
      // optional group memberships
      for (const gid of f.groupIds) {
        try { await api.post(`/groups/${gid}/members?workspace_id=${workspaceId}`, { entity_ids: [ent.id] }); }
        catch (e) { /* non-fatal */ }
      }
      localStorage.removeItem(DRAFT_KEY);
      toast.success("Contact created");
      onCreated(ent.id);
    } catch (err) {
      if (err.sessionExpired) {
        stashDraft();
        toast.error("Your session expired. Your draft is saved — sign in again, reopen Add Contact, and it will be restored.");
      } else if (err.response?.status === 422) {
        toast.error(apiError(err.response?.data?.detail) || "Please check the form fields.");
      } else {
        toast.error(apiError(err.response?.data?.detail) || "Could not save. Please try again.");
      }
      setBusy(false);
    }
  };

  return (
    <Modal open onClose={onClose} title="Add Contact" testid="create-entity-modal" width={620}>
      {contextLabel && <div className="si-label mb-3" style={{ color: "var(--si-teal-light)" }}>{contextLabel}</div>}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div className="sm:col-span-2"><Field label="Name" hint="Minimal identification is fine."><Input data-testid="ce-name" value={f.name} onChange={(e) => set("name", e.target.value)} autoFocus /></Field></div>
        <Field label="Type"><Select data-testid="ce-type" value={f.type} onChange={(e) => set("type", e.target.value)}>{ETYPES.map((t) => <option key={t} value={t}>{t}</option>)}</Select></Field>
      </div>

      <ContactMethodsEditor key={draftRestored ? "draft" : "fresh"} value={f.contact_methods} onChange={(cm) => set("contact_methods", cm)} idPrefix="ce" />

      <Field label="Website"><Input data-testid="ce-website" value={f.website} onChange={(e) => set("website", e.target.value)} placeholder="https://…" /></Field>
      <Field label="Address"><Textarea data-testid="ce-address" value={f.address} onChange={(e) => set("address", e.target.value)} placeholder="Street, city, region, country" /></Field>
      <Field label="Description / notes"><Textarea data-testid="ce-description" value={f.description} onChange={(e) => set("description", e.target.value)} /></Field>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Field label="Spider network (optional)"><Select data-testid="ce-origin" value={f.originId} onChange={(e) => set("originId", e.target.value)}><option value="">— None —</option>{origins.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}</Select></Field>
        <Field label="Add to group (optional)"><Select data-testid="ce-group" value={f.groupIds[0] || ""} onChange={(e) => set("groupIds", e.target.value ? [e.target.value] : [])}><option value="">— None —</option>{groups.map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}</Select></Field>
      </div>

      <Collapsible label="Advanced (coordinates, stage, verification, tags)" testid="ce-advanced-toggle">
        <Field label="Tags (comma separated)"><Input data-testid="ce-tags" value={f.tags} onChange={(e) => set("tags", e.target.value)} placeholder="retail, food" /></Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Relationship stage"><Select value={f.relationship_stage} onChange={(e) => set("relationship_stage", e.target.value)}>{STAGES.map((t) => <option key={t} value={t}>{t.replace(/_/g, " ")}</option>)}</Select></Field>
          <Field label="Verification"><Select value={f.verification_status} onChange={(e) => set("verification_status", e.target.value)}>{VERIFS.map((t) => <option key={t} value={t}>{t}</option>)}</Select></Field>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Latitude"><Input value={f.lat} onChange={(e) => set("lat", e.target.value)} /></Field>
          <Field label="Longitude"><Input value={f.lng} onChange={(e) => set("lng", e.target.value)} /></Field>
        </div>
      </Collapsible>

      <Btn variant="primary" className="w-full" onClick={save} disabled={busy} data-testid="ce-save">{busy ? "Saving…" : "Create contact"}</Btn>
    </Modal>
  );
}
